import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Receipt } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { MoneyAmount } from "./MoneyAmount";
import type { GroupListItem } from "../api/types";

/**
 * Group summary tile used on the Dashboard and Groups pages. Shared so the
 * two grids stay visually identical instead of drifting apart over time.
 */
export function GroupCard({ group }: { group: GroupListItem }): ReactNode {
  const owing = Math.max(-group.balance, 0);
  const owed = Math.max(group.balance, 0);

  return (
    <Link to={`/groups/${group.id}`} className="block">
      <Card className="h-full border border-outline-variant/60 transition-colors hover:border-primary/50 hover:bg-surface-container/40">
        <CardContent className="flex h-full flex-col gap-6 p-6">
          <div className="flex items-start justify-between">
            <span className="grid size-10 place-items-center rounded-sm bg-primary-fixed text-primary">
              <Receipt className="size-5" />
            </span>
            <span className="rounded-full bg-surface-container px-3 py-1 text-label-sm text-on-surface-variant">
              {group.currency}
            </span>
          </div>
          <div>
            <p className="text-headline-md">{group.label}</p>
            <p className="mt-2 text-label-md text-on-surface-variant">
              Updated {new Date(group.updatedAt).toLocaleDateString()}
            </p>
          </div>
          <div className="mt-auto grid grid-cols-2 gap-4 border-t border-outline-variant/40 pt-4">
            <div>
              <p className="text-label-sm text-on-surface-variant">You owe</p>
              <MoneyAmount
                minor={owing}
                currency={group.currency}
                className="mt-1 text-title-md text-error"
              />
            </div>
            <div className="text-right">
              <p className="text-label-sm text-on-surface-variant">
                You are owed
              </p>
              <MoneyAmount
                minor={owed}
                currency={group.currency}
                className="mt-1 text-title-md"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
